import React, { useEffect, useState, useRef, useMemo } from 'react';
import './TVDisplay.css';
import GraphRNDDash from './GraphRNDDash';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux';
import { MenuItem, Select } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import TvIcon from '@mui/icons-material/Tv';
import { toggleDashModeInTab } from '../../redux/TabTile/TabActions';
import { toggleDashMode } from '../../redux/TabTile/TabTileActionsAndMultipleDispatches';
import { ColorSchemes } from '../ChartOptions/Color/ColorScheme';
import { motion, AnimatePresence } from 'framer-motion';
import ChartData from '../ChartAxes/ChartData';
import TabRibbon from '../TabsAndTiles/TabRibbon';
import Button from './DraggableResizeButton/Button';
import Image from './Image';
import useCombinedColorSchemes from '../../hooks/useCombinedColorSchemes';
import RichText from './RichText/RichText';

const TVDisplay: React.FC = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const state = location.state || {};
  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  const { tabState, chartControls, tabTileProps, tvSettings, tabId, defaultThemeId } = useSelector(
    (state: RootState) => ({
      tabState: state.tabState,
      chartControls: state.chartControls,
      tabTileProps: state.tabTileProps,
      tvSettings: state.tabTileProps.tvSettings,
      tabId: state.tabTileProps.selectedTabId,
      defaultThemeId:
        state.tabState.tabs[state.tabTileProps.selectedTabId]?.dashboardState?.defaultThemeId,
    }),
  );
  const { selectedTabArray, transitionTime } = tvSettings;
  const selectedTabs = selectedTabArray || [];
  const workspaceId = searchParams.get('workspaceId');
  const playbookId = searchParams.get('playbookId');

  const transitionInterval = transitionTime;
  const goBackToDashboard = () => {
    navigate(`/playbook?workspaceId=${workspaceId}&playbookId=${playbookId}`, {
      state: {
        ...state,
        mode: 'fromTVDisplay',
      },
    });
  };
  useEffect(() => {
    if (selectedTabs.length > 1) {
      const intervalId = setInterval(() => {
        setCurrentTabIndex((prevIndex) => (prevIndex + 1) % selectedTabs.length);
      }, transitionInterval * 1000);

      return () => clearInterval(intervalId); // cleanup on unmount
    } else if (selectedTabs.length === 0) {
      goBackToDashboard();
    }
  }, [selectedTabs, transitionInterval]);

  const activeTabId = selectedTabs[currentTabIndex];

  const dashTheme = tabState.tabs[activeTabId]?.dashboardState?.colorScheme
    ? tabState.tabs[activeTabId]?.dashboardState?.colorScheme
    : 'peacock';
  const theme = tabState.tabs[activeTabId]?.dashboardState?.theme
    ? tabState.tabs[activeTabId]?.dashboardState?.theme
    : 'FlatUI';

  const [bckgroundColor, setBackgroundColor] = useState('#ffffff');

  const scheme_passed = tabState.tabs[tabId]?.dashboardState?.colorScheme ?? 'peacock';

  const combinedThemes = useCombinedColorSchemes();

  const [innerDimensions, setinnerDimensions] = useState<any>({});
  const [schemeColor, setSchemeColor] = useState<string>(scheme_passed);
  const [softUI, setSoftUI] = useState<boolean>(theme === 'SoftUI');
  const targetRef = useRef<HTMLDivElement>(null);
  const [selectOpen, setSelectOpen] = useState(false);
  const [disableEdit, setDisableEdit] = useState<boolean>(state.disableEdit || false);
  const [pageSettings] = useState({ callForDownload: false });
  const [ApplytoTiles] = useState(false);
  const mouseDownOutsideGraphs = () => {};
  const [menuBarVisible, setMenuBarVisible] = useState(true);

  useEffect(() => {
    if (!combinedThemes.length) return;

    let activeTheme = combinedThemes.find(
      (theme) => theme.name.toLowerCase() === scheme_passed.toLowerCase(),
    );

    if (!activeTheme && defaultThemeId) {
      activeTheme = combinedThemes.find(
        (theme) => theme.id === defaultThemeId || theme.themeId === defaultThemeId,
      );
    }

    setBackgroundColor(activeTheme?.background || '#ffffff');
  }, [scheme_passed, defaultThemeId, combinedThemes]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const resetTimer = () => {
      setMenuBarVisible(true); // Show on movement
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setMenuBarVisible(false); // Hide after 15 seconds of inactivity
      }, 15000); // 15 seconds
    };

    // Set up event listeners
    window.addEventListener('mousemove', resetTimer);
    window.addEventListener('mousedown', resetTimer);
    window.addEventListener('keypress', resetTimer);
    window.addEventListener('touchstart', resetTimer);

    resetTimer(); // Start initial timer

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('mousemove', resetTimer);
      window.removeEventListener('mousedown', resetTimer);
      window.removeEventListener('keypress', resetTimer);
      window.removeEventListener('touchstart', resetTimer);
    };
  }, []);

  const menuBarStyle = {
    //setting the menubar color
    backgroundColor: bckgroundColor,
    color: bckgroundColor === '#000000' ? '#ffffff' : '#000000', // smart contrast
  };

  const dashboardAreaStyle = {
    //setting the background color
    ...(pageSettings.callForDownload
      ? {
          height: '100vh',

          borderTop: '2px solid rgba(224,224,224,1)',
          boxShadow: 'none',
        }
      : { height: '100vh' }),
    backgroundColor: bckgroundColor,
    width: '100%',
  };

  const [dashStyle, setdashStyle] = useState<any>({
    //used for softui/flatui
    width: innerDimensions.width,
    height: innerDimensions.height,
  });

  const [style, setStyle] = useState<any>({
    //used for softui/flatui
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'solid 1px transparent',
    backgroundColor: 'white',
    boxSizing: 'border-box',
    zIndex: 10,
  });

  const [style2, setStyle2] = useState<any>({
    //used for softui/flatui
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'solid 1px darkGray',
    backgroundColor: 'white',
    boxSizing: 'border-box',
    zIndex: 20,
  });

  const getBoxShadow = (schemeColor: any) => {
    //setting boxshadows
    if (schemeColor === 'dark') {
      return '0 0 20px rgba(0,0,0,0.6)';
    } else if (schemeColor === 'chalk') {
      return '0 0 18px rgba(0,0,0,0.4)';
    } else if (schemeColor === 'halloween') {
      return '0 0 15px rgba(0,0,0,0.3)';
    } else if (schemeColor === 'purplePassion') {
      return '0 0 20px rgba(0,0,0,0.5)';
    }
    return '0 0 15px rgba(0,0,0,0.12)';
  };

  const tileskeuomorphicStyle = {
    boxShadow: getBoxShadow(schemeColor), // backgroundColor is dynamically passed
    borderRadius: '12px',
  };
  const skeuomorphicInnerStyle = {
    boxShadow: 'inset 4px 4px 8px rgba(0,0,0,0.2), inset -4px -4px 8px rgba(255,255,255,0.7)',
    borderRadius: '8px',
  };

  useEffect(() => {
    //the softUi is applied taking every style/colorScheme into consideration
    if (softUI) {
      setdashStyle((prevStyle: any) => ({
        ...prevStyle,
        backgroundColor: bckgroundColor,
        boxShadow: softUI ? '0 0 15px rgba(0,0,0,0.12)' : 'none',
      }));
      setStyle((prevStyle: any) => ({
        ...prevStyle,
        backgroundColor: bckgroundColor,
        ...tileskeuomorphicStyle,
      }));
      setStyle2((prevStyle: any) => ({
        ...prevStyle,
        backgroundColor: bckgroundColor,
        ...skeuomorphicInnerStyle,
      }));
    } else {
      setdashStyle((prevStyle: any) => ({
        ...prevStyle,
        boxShadow: 'none',
        backgroundColor: bckgroundColor,
        borderRadius: '0px',
      }));
      setStyle((prevStyle: any) => ({
        ...prevStyle,
        boxShadow: 'none',
        backgroundColor: bckgroundColor,
        borderRadius: '0px',
      }));
      setStyle2((prevStyle: any) => ({
        ...prevStyle,
        boxShadow: 'none',
        backgroundColor: bckgroundColor,
        borderRadius: '0px',
      }));
    }
  }, [bckgroundColor, softUI]);

  const renderGraphs = () => {
    const tiles = tabState.tabs?.[activeTabId]?.tilesInDashboard || [];

    return (
      <>
        {tiles.map((box: string, index: number) => {
          const boxDetails = tabState.tabs?.[activeTabId]?.dashTilesDetails?.[box];
          let colorScheme = chartControls.properties?.[box]?.colorScheme;
          if (!ApplytoTiles) {
            colorScheme = scheme_passed;
          }

          return (
            <GraphRNDDash
              key={index}
              softUI={softUI}
              style={softUI ? tileskeuomorphicStyle : ''}
              backgroundColor={bckgroundColor}
              dashboardDimensions={{ width: dashStyle.width, height: dashStyle.height }}
              colorScheme={colorScheme}
              mouseDownOutsideGraphs={mouseDownOutsideGraphs}
              tabId={activeTabId}
              boxDetails={boxDetails}
              setStyle={setStyle}
              style2={style2}
              setStyle2={setStyle2}
              gridSize={{ x: dashStyle.width, y: dashStyle.height }}
            />
          );
        })}

        <Button
          currentTabId={activeTabId}
          backgroundColor={bckgroundColor}
          softUI={softUI}
          dashMode={tabTileProps.dashMode}
          tabState={tabState}
          colorScheme={schemeColor}
        />

        <Image
          softUI={softUI}
          backgroundColor={bckgroundColor}
          dashMode={tabTileProps.dashMode}
          currentTabId={activeTabId}
          colorScheme={schemeColor}
        />

        <RichText
          setActiveEditor={() => {}}
          onEditorChange={() => {}}
          softUI={softUI}
          backgroundColor={bckgroundColor}
          dashMode={tabTileProps.dashMode}
          currentTabId={activeTabId}
          colorScheme={schemeColor}
        />
      </>
    );
  };

  return (
    <>
      {/* Top Menu Bar */}
      {menuBarVisible && (
        <div className="menu-bar" style={menuBarStyle}>
          <img src="/logo.png" alt="App Logo" className="logo" />

          <div className="userInfo dropdown-container">
            {tabState.tabs[tabTileProps.selectedTabId].showDash || tabTileProps.showDash ? (
              <Select
                size="small"
                open={selectOpen}
                onOpen={() => setSelectOpen(true)}
                onClose={() => {
                  setSelectOpen(false);
                  setTimeout(() => {
                    if (document.activeElement instanceof HTMLElement) {
                      document.activeElement.blur();
                    }
                  }, 0);
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: bckgroundColor,
                    },
                  },
                }}
                sx={{
                  height: '1.5rem',
                  fontSize: '12px',
                  width: '8.2rem',
                  margin: 'auto 0.5rem',
                  paddingTop: '2.8px',
                  borderRadius: '2px',
                  backgroundColor: bckgroundColor,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: selectOpen ? '#2bb9bb' : '#929292',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: selectOpen ? '#2bb9bb' : '#929292',
                  },
                  '& .MuiSelect-icon': {
                    color: '#929292',
                  },
                }}
                value={tabTileProps.dashMode}
                onChange={(e) => {
                  dispatch(toggleDashMode(e.target.value));
                  dispatch(toggleDashModeInTab(tabTileProps.selectedTabId, e.target.value));
                  goBackToDashboard();
                }}
                renderValue={(selected) => {
                  const iconStyles = {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    paddingTop: '2px',
                    paddingBottom: '2px',
                    color: '#929292',
                  };

                  switch (selected) {
                    case 'Edit':
                      return (
                        <div style={iconStyles}>
                          <EditIcon fontSize="medium" />
                        </div>
                      );
                    case 'Present':
                      return (
                        <div style={iconStyles}>
                          <SlideshowIcon fontSize="medium" />
                        </div>
                      );
                    case 'TV Display':
                      return (
                        <div style={iconStyles}>
                          <TvIcon fontSize="medium" />
                        </div>
                      );
                    default:
                      return selected;
                  }
                }}
              >
                {!disableEdit && (
                  <MenuItem
                    sx={{
                      fontSize: '12px',
                      padding: '2px 8px',
                      margin: 0,
                      color: '#929292',
                    }}
                    value="Edit"
                  >
                    <EditIcon fontSize="small" sx={{ marginRight: '4px' }} />
                    {selectOpen && 'Edit'}
                  </MenuItem>
                )}
                <MenuItem
                  sx={{
                    fontSize: '12px',
                    padding: '2px 8px',
                    margin: 0,
                    color: '#929292',
                  }}
                  value="Present"
                >
                  <SlideshowIcon fontSize="small" sx={{ marginRight: '4px' }} />
                  {selectOpen && 'Present'}
                </MenuItem>
                <MenuItem
                  sx={{
                    fontSize: '12px',
                    padding: '2px 8px',
                    margin: 0,
                    color: '#929292',
                  }}
                  value="TV Display"
                >
                  <TvIcon fontSize="small" sx={{ marginRight: '4px' }} />
                  {selectOpen && 'TV Display'}
                </MenuItem>
              </Select>
            ) : null}
          </div>
        </div>
      )}

      {/* Slideshow Content */}
      <div className="dashboardOuters" ref={targetRef}>
        <TabRibbon
          selectedTab={activeTabId}
          backgroundColor={bckgroundColor}
          tabList={selectedTabArray}
        ></TabRibbon>
        <ChartData
          tabId={tabTileProps.selectedTabId}
          tileId={tabTileProps.selectedTileId}
          screenFrom="TV_Display"
        ></ChartData>
        <div
          id="GraphAreaToDownloads"
          className={`dashboardAreas ${softUI ? 'skeuomorphic' : ''}`}
          style={dashboardAreaStyle}
        >
          <AnimatePresence mode="wait">
            <div
              style={{ position: 'relative', width: '100%', height: '100%', paddingTop: '4rem' }}
            >
              <motion.div
                key={activeTabId}
                variants={{
                  enter: { x: '100%', opacity: 0 },
                  center: { x: 0, opacity: 1 },
                  exit: { x: '-100%', opacity: 0 },
                }}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.8, ease: 'easeInOut' }}
                className="tab-content"
                style={{
                  width: '100%',
                  height: '100%',
                  position: 'absolute', // Important
                  top: 0,
                  left: 0,
                }}
              >
                {tabState.tabs?.[activeTabId]?.tilesInDashboard?.length > 0 ? (
                  renderGraphs()
                ) : (
                  <div
                    className={softUI ? 'skeuomorphic-inner' : ''}
                    style={{
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#999999',
                      marginTop: '3rem',
                      borderTop: pageSettings.callForDownload
                        ? '2px solid rgba(224,224,224,1)'
                        : '0px',
                    }}
                  >
                    <pre style={{ fontSize: '16px' }}>
                      {tabTileProps.dashMode === 'Edit'
                        ? `No graphs selected\n\nSelect tiles from right panel to place graph here`
                        : `No graphs selected`}
                    </pre>
                  </div>
                )}
              </motion.div>
            </div>
          </AnimatePresence>
        </div>
      </div>
    </>
  );
};

export default TVDisplay;
